// @givmo/employer-module — the Security surface's two dark-gate probes, and the store every host reads
// them through (AS-WEB4/6; their own module since AS-WEB20, so the Employees roster reads the
// requirement's signal without importing the panels).
//
// Each probe asks its section's own GET once per page load — keyed on a 200 arriving, silent,
// memoised — and notifies the store when it settles. Why each of those holds is in
// `settings-security-tab.tsx`'s header, beside the panels the probes gate.

import { useEffect, useSyncExternalStore } from 'react';
import { probeAuthPolicy } from './auth-policy-transport';
import { readEmployeesWithoutSecondFactor } from './company-security-decode';
import { probeSsoConfig } from './sso-config-transport';

// ── Visibility ──────────────────────────────────────────────────────────────────────────────────

/** The dark-gate verdict. `unknown` until the probe settles, and `unknown` never reveals. */
export type AuthPolicyVerdict = 'unknown' | 'available' | 'unavailable';

let verdict: AuthPolicyVerdict = 'unknown';
let probedPolicy: unknown = null;
let inFlight: Promise<void> | null = null;

/** The settled verdict for this page load. Exposed for tests and for the tab's own seeding. */
export function authPolicyVerdict(): AuthPolicyVerdict {
  return verdict;
}

/**
 * Drop the memoised probe. A TEST SEAM — production never calls it, because a flag flip arrives with
 * a fresh document anyway. Without it the first suite to settle the probe would decide the answer
 * for every suite after it in the same module registry (the passkey probe carries the same seam).
 */
export function resetAuthPolicyProbe(): void {
  verdict = 'unknown';
  probedPolicy = null;
  inFlight = null;
}

/**
 * Ask the server once per page load, then notify the hosts (`subscribeSecurityProbes`).
 *
 * `probeAuthPolicy` never rejects — it resolves to a verdict — so there is no rejection path to
 * handle and nothing is ever logged. The memo covers React's development double-mount and the fact
 * that a permission predicate runs on every render of the page.
 */
function ensureAuthPolicyProbe(): void {
  if (verdict !== 'unknown' || inFlight !== null) return;
  inFlight = probeAuthPolicy().then((result) => {
    verdict = result.available ? 'available' : 'unavailable';
    probedPolicy = result.body;
    notifySecurityProbesChanged();
  });
}

// ── The SSO section's own dark gate (AS-WEB6) ───────────────────────────────────────────────────
//
// A SECOND, INDEPENDENT probe, because the two halves of this tab are behind two different backend
// flags: the MFA editor rides `EMPLOYER_AUTH_POLICY_ENABLED` and the SSO section rides
// `EMPLOYER_SSO_ENABLED`, and nothing makes them flip together. Same mechanics as the one above —
// keyed on a 200 arriving, silent, memoised per page load, and it notifies the registry when it
// settles.

let ssoVerdictValue: AuthPolicyVerdict = 'unknown';
let probedSsoConfigBody: unknown = null;
let ssoInFlight: Promise<void> | null = null;

/** The settled SSO verdict for this page load. Exposed for tests and for the tab's own seeding. */
export function ssoConfigVerdict(): AuthPolicyVerdict {
  return ssoVerdictValue;
}

/** Test seam, exactly like {@link resetAuthPolicyProbe}. Production never calls it. */
export function resetSsoConfigProbe(): void {
  ssoVerdictValue = 'unknown';
  probedSsoConfigBody = null;
  ssoInFlight = null;
}

function ensureSsoConfigProbe(): void {
  if (ssoVerdictValue !== 'unknown' || ssoInFlight !== null) return;
  ssoInFlight = probeSsoConfig().then((result) => {
    ssoVerdictValue = result.available ? 'available' : 'unavailable';
    probedSsoConfigBody = result.body;
    notifySecurityProbesChanged();
  });
}

// ── Either surface: the page exists (AS-WEB6; both hosts since AS-WEB8) ─────────────────────────
//
// ⚠ EITHER SURFACE REVEALS THE SECURITY SURFACE (AS-WEB6). It used to be the auth-policy probe alone,
// which was right while that was the only thing here and is a trap now: the two sections are behind
// two independent backend flags, so an environment running `EMPLOYER_SSO_ENABLED` without
// `EMPLOYER_AUTH_POLICY_ENABLED` would hide the whole surface and make the SSO configuration
// unreachable — a feature that is on and cannot be found. Each SECTION still renders only on its own
// verdict (see {@link SettingsSecurityTab}), so revealing on either can never show a surface whose
// writes would 404.
//
// ⚠ THE PROBES START ONLY WHEN A HOST ASKS (`ensureSecurityProbes`), and each host asks only for
// someone who may use the surface: the portal's tab for an Owner, the console for a holder of
// `write_settings` on an Admin Console page. A Member never spends a request on a surface they could
// not use, and no other page pays for them. The requests are fire-and-forget outside React state;
// their settling reaches React through the store below, which is exactly what such a store is for.

const probeListeners = new Set<() => void>();

/** Tell every listener a probe settled — each re-reads the verdicts. */
function notifySecurityProbesChanged(): void {
  // A copy: a listener that subscribes another during the pass must not be called by this one.
  for (const listener of [...probeListeners]) listener();
}

/**
 * Listen for a probe settling, outside React (the portal's tab registry hands the notice on to its
 * own store). Returns the unsubscribe.
 */
export function subscribeSecurityProbes(listener: () => void): () => void {
  probeListeners.add(listener);
  return () => {
    probeListeners.delete(listener);
  };
}

/** Start both probes — once per page load, however often a host asks. */
export function ensureSecurityProbes(): void {
  ensureAuthPolicyProbe();
  ensureSsoConfigProbe();
}

/**
 * Whether this environment serves the Security surface at all: `available` once EITHER probe has
 * answered 200, `unavailable` once BOTH have settled without one, `unknown` until then — and
 * `unknown` never reveals. Reads only; a host starts the probes ({@link ensureSecurityProbes}).
 */
export function securitySurfaceVerdict(): AuthPolicyVerdict {
  if (verdict === 'available' || ssoVerdictValue === 'available') return 'available';
  if (verdict === 'unavailable' && ssoVerdictValue === 'unavailable') return 'unavailable';
  return 'unknown';
}

/**
 * {@link securitySurfaceVerdict}, live: re-renders when the verdict moves, and starts the probes once
 * `start` holds (a host's own gate — the console starts them for a holder of `write_settings` on an
 * Admin Console page). Nothing starts them otherwise, so the verdict stays `unknown` until a host has
 * asked; once settled it stands for the page load, wherever the host reads it.
 */
export function useSecuritySurfaceVerdict(start: boolean): AuthPolicyVerdict {
  const current = useSyncExternalStore(
    subscribeSecurityProbes,
    securitySurfaceVerdict,
    securitySurfaceVerdict,
  );
  useEffect(() => {
    if (start) ensureSecurityProbes();
  }, [start]);
  return current;
}

/**
 * {@link securitySurfaceVerdict}, held at `unknown` until BOTH probes have settled — for a host that
 * draws the panels on a page of their own (the console's Security page, AS-WEB8). The first 200
 * reveals the surface, but the panels hide the two-factor half only on its own settled 404, so
 * drawing them before the other probe settles would show that half, and send its roster sample,
 * where it is not served. Waiting costs only the slower of two requests that go out together.
 */
export function settledSecuritySurfaceVerdict(): AuthPolicyVerdict {
  if (verdict === 'unknown' || ssoVerdictValue === 'unknown') return 'unknown';
  return securitySurfaceVerdict();
}

/** {@link settledSecuritySurfaceVerdict}, live — and starts the probes once `start` holds. */
export function useSettledSecuritySurfaceVerdict(start: boolean): AuthPolicyVerdict {
  const current = useSyncExternalStore(
    subscribeSecurityProbes,
    settledSecuritySurfaceVerdict,
    settledSecuritySurfaceVerdict,
  );
  useEffect(() => {
    if (start) ensureSecurityProbes();
  }, [start]);
  return current;
}

/**
 * Both probes' verdicts, as one value that moves on EVERY settling. The panels read it so each half
 * appears the moment its own probe answers, whichever host renders them and whatever else re-renders.
 */
export function useProbeVerdicts(): string {
  return useSyncExternalStore(subscribeSecurityProbes, probeVerdicts, probeVerdicts);
}

function probeVerdicts(): string {
  return `${verdict}:${ssoVerdictValue}`;
}

/** The auth-policy probe's 200 body for this page load, or `null`. */
export function probedAuthPolicy(): unknown {
  return probedPolicy;
}

/** The SSO config probe's 200 body for this page load, or `null`. */
export function probedSsoConfig(): unknown {
  return probedSsoConfigBody;
}

// ── AS-WEB20: the requirement's signal, where the panels are not ────────────────────────────────

/**
 * Whether the company two-factor requirement is live for this page load: the auth-policy probe's
 * body carries AS-BE49's count key (`employees_without_second_factor`) — the signal the panels'
 * Required text reveals on. `start` starts that one probe (the roster starts it only once its
 * two-factor column shows, so a page asking nobody spends nothing); `false` until it has answered
 * with the key, and for the page load once it has answered without.
 */
export function useTwoFactorRequirementLive(start: boolean): boolean {
  const live = useSyncExternalStore(
    subscribeSecurityProbes,
    requirementLiveNow,
    requirementLiveNow,
  );
  useEffect(() => {
    if (start) ensureAuthPolicyProbe();
  }, [start]);
  return live;
}

function requirementLiveNow(): boolean {
  return readEmployeesWithoutSecondFactor(probedPolicy) !== null;
}
